import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import CoachingToDo from '../../../../../components/CoachingToDo/CoachingToDo';
import CoachingShoutOut from '../../../../../components/coachingShoutOut/coachingShoutOUt';
import { AGENTROLE, MANAGERROLE, ROLES, SUPERVISORROLE } from '../../../../../constants';
import { selectCurrentEmpid } from '../../../../../pages/login/authSlice';
import { changeDateFormate, datetimeToDate } from '../../../../../utils/dateFormat';
import { useCoachingGoalReviewMutation } from '../../goalSlice';
import { GoalExpansionTemplateProps } from '../types';
import {
    checkAscGoalDesc,
    checkPayout,
    checkTarget,
    checkTlGoalDesc,
    createGoalReviewPayload,
} from '../validateHelperFunctions';
import DescriptionBox from '../../../../../components/descriptionBox/DescriptionBox';

const findAuthorRole = (level: string) => {
    if (ROLES.Agents?.includes(level)) return AGENTROLE.charAt(0).toUpperCase() + AGENTROLE.slice(1);
    else if (ROLES.Supervisor?.includes(level))
        return SUPERVISORROLE.charAt(0).toUpperCase() + SUPERVISORROLE.slice(1);
    else if (ROLES.Manager?.includes(level))
        return MANAGERROLE.charAt(0).toUpperCase() + MANAGERROLE.slice(1);
    else return '';
};

export const GoalReview = (props: GoalExpansionTemplateProps) => {
    const [CoachingGoalReview] = useCoachingGoalReviewMutation();
    const currEmpId = useSelector(selectCurrentEmpid);
    const isAuthor = props.data?.author_employee_number === currEmpId;
    const authorRole = findAuthorRole(props.data?.author_level);
    const recipienteRole = findAuthorRole(props.data?.recipient_level);
    const [tlGoalDesc, setTLGoalDesc] = useState<string>(
        props.data.review?.author_month_end_review_comment || ''
    );
    const [ascGoalDesc, setAscGoalDesc] = useState<string>(
        props.data.review?.response_month_end_review_comment || ''
    );
    const [reviewed, setReviewed] = useState<boolean>(props.data?.reviewed);
    const [results, setResults] = useState<string>(props.data?.actual_goal_value || '');
    const [payout, setPayout] = useState<string>(props.data?.review?.actual_payout || '');
    const [todoVisible, setTodoVisible] = useState(false);
    const [shoutoutVisible, setShoutoutVisible] = useState(false);
    const handleTextChange = (e: any, type: 'tlGoalDesc' | 'ascGoalDesc' | 'results' | 'payout') => {
        const eventVal = e.target.value;
        if (type === 'ascGoalDesc') setAscGoalDesc(eventVal);
        else if (type === 'tlGoalDesc') setTLGoalDesc(eventVal);
        else if (type === 'results') {
            if (!isNaN(parseFloat(eventVal)) || eventVal === '.' || eventVal === '0') setResults(eventVal);
        } else if (type === 'payout') {
            if (!isNaN(parseFloat(eventVal)) || eventVal === '.' || eventVal === '0') setPayout(eventVal);
        }
    };
    const validate = () => {
        const errors: string[] = [];

        if (reviewed) {
            checkTlGoalDesc(tlGoalDesc, errors);
            checkAscGoalDesc(ascGoalDesc, errors);
            checkTarget(results, errors);
            checkPayout(payout, errors);
        }
        if (isAuthor) {
            checkTlGoalDesc(tlGoalDesc, errors);
            checkTarget(results, errors);
            checkPayout(payout, errors);
            if (ascGoalDesc.trim().length !== 0) {
                checkAscGoalDesc(ascGoalDesc, errors);
            }
        } else {
            checkAscGoalDesc(ascGoalDesc, errors);
        }

        if (errors.length > 0) {
            errors.forEach((error) => toast.error(error));
            return false;
        }
        return true;
    };
    const handleSave = () => {
        if (!validate()) return;
        const payload: any = createGoalReviewPayload(
            props.data,
            results,
            payout,
            tlGoalDesc,
            ascGoalDesc,
            reviewed,
            currEmpId
        );
        if (Object.keys(payload).length <= 1) {
            toast.warn('No changes made');
            return;
        }
        CoachingGoalReview(payload)
        .unwrap()
        .then((res: any) => {
            if (res?.data) toast.success('Goal Review Saved Successfully');
            else toast.error('Failed to save goal review');
        })
        .catch(() => {
            toast.error('Failed to save goal review');
        });
    };
    return (
        <div className="p-2">
            <DescriptionBox
                title="TL/Supervisor Month-End Review Comments"
                value={tlGoalDesc}
                responseDescription={tlGoalDesc}
                disabled={!isAuthor || props.data.reviewed}
                limit={200}
                onChange={(value) => {
                    const syntheticEvent = {
                        target: { value },
                    };
                    handleTextChange(syntheticEvent, 'tlGoalDesc');
                }}
            />

            <div className="d-flex justify-content-between mt-3">
                <div className="d-flex">
                    <div className="me-4">
                        <h6>Date</h6>
                        <div className="mt-3">
                            {props.data.review?.author_review_date
                                ? changeDateFormate(datetimeToDate(props.data.review?.author_review_date))
                                : changeDateFormate(datetimeToDate(new Date()))}
                        </div>
                    </div>
                    <span>
                        <h6>Results</h6>
                        <InputText
                            className="borderRadius8  me-4"
                            value={results}
                            onChange={(e) => handleTextChange(e, 'results')}
                            disabled={!isAuthor || props.data.reviewed}
                        />
                    </span>
                    <span>
                        <h6>Payout</h6>
                        <InputText
                            className="borderRadius8 me-4"
                            value={payout}
                            onChange={(e) => handleTextChange(e, 'payout')}
                            disabled={!isAuthor || props.data.reviewed}
                        />
                    </span>
                    <div className="me-4">
                        <h6>Role</h6>
                        <div className="mt-3">{authorRole}</div>
                    </div>
                </div>

                <div className="mt-3">
                    <CoachingShoutOut
                        receiverId={props.receiverId}
                        receiverName={props.receiverName}
                        visible={shoutoutVisible}
                        setVisible={setShoutoutVisible}
                        coachingEntityTypeId={5}
                        coachingEntityId={props.goalId ?? 0}
                        coreValueId={0}
                    />
                </div>
            </div>
            <div className="splitSection mt-3 mb-3" />
            <DescriptionBox
                title="Agent Month-End Review Comments"
                value={ascGoalDesc}
                responseDescription={ascGoalDesc}
                disabled={props.data.reviewed}
                limit={200}
                onChange={(value) => {
                    const syntheticEvent = {
                        target: { value },
                    };
                    handleTextChange(syntheticEvent, 'ascGoalDesc');
                }}
            />

            <div className="d-flex justify-content-start mt-2">
                <div className="me-4">
                    <h6>Date</h6>
                    <div className="mt-3">
                        {props.data.reviewed && props.data.review?.response_review_date
                            ? changeDateFormate(datetimeToDate(props.data.review?.response_review_date))
                            : changeDateFormate(datetimeToDate(new Date()))}
                    </div>
                </div>
                <div className="ms-4 me-4">
                    <h6>Role</h6>
                    <div className="mt-3">{recipienteRole}</div>
                </div>

                <div className="ms-4">
                    <h6>Reviewed</h6>
                    <Checkbox
                        className="mt-2"
                        checked={reviewed}
                        onChange={(e) => setReviewed(e.target.checked)}
                        disabled={props.data.reviewed}
                    />
                </div>
            </div>
            <div className="d-flex justify-content-end">
                <Button
                    label="Cancel"
                    onClick={() => props.handleCancel(props.data.coaching_goal_id)}
                    className="btn-secondary me-2"
                />
                <Button
                    label="Save"
                    onClick={handleSave}
                    className="btn-primary me-2"
                    disabled={props.data.reviewed}
                />
                <CoachingToDo
                    receiverId={props.receiverId}
                    receiverName={props.receiverName}
                    visible={todoVisible}
                    setVisible={setTodoVisible}
                    coachingEntityTypeId={5}
                    coachingEntityId={props?.goalId}
                    coachingStatusId={1}
                />
            </div>
        </div>
    );
};
